import { Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, CheckCircle2, Users, Heart, GraduationCap, Scale } from "lucide-react";
import { useT, LangToggle } from "../i18n/LanguageContext";

const LOGO = "https://customer-assets.emergentagent.com/job_equality-hub-7/artifacts/3m4xmkko_mother%20hippo%20PNG.png";

const copy = {
  en: {
    back: "Selector",
    nav: { mission: "Mission", programs: "Programs", impact: "Impact", stories: "Stories", donate: "Support our work" },
    badge: "Registered International NGO",
    heroA: "Empowering girls.",
    heroAccent: "Transforming",
    heroB: " communities.",
    heroSub: "The Mother Hippo Foundation is dedicated to the total eradication of Female Genital Mutilation through community-led education, survivor healthcare, and evidence-based global advocacy.",
    support: "Support our work",
    report: "View 2025 Impact Report",
    quickStats: [
      { v: "230K+", l: "Lives reached" },
      { v: "47", l: "Villages" },
      { v: "12", l: "Countries" },
    ],
    verified: "VERIFIED 2025",
    verifiedP: "Independently audited — 89¢ of every dollar funds programs.",
    coalition: "In coalition with",
    partners: ["UN Women", "WHO", "Amnesty", "Equality Now", "Plan International", "UNICEF"],
    missionKicker: "Our strategic mission",
    missionHeading: "A world where every girl authors her own life.",
    missionP1: "Female Genital Mutilation affects over 200 million women and girls alive today. It is a public health crisis, a human rights violation, and one of the most solvable injustices of our time.",
    missionP2a: "We work at three levels — ",
    missionP2b: "community",
    missionP2c: ", ",
    missionP2d: "clinical",
    missionP2e: ", and ",
    missionP2f: "policy",
    missionP2g: " — with local partners who know the terrain, and with international coalitions that can hold governments to account.",
    missionP3: "Our 2030 commitment is measurable: a 75% reduction in FGM prevalence across our partner regions, tracked through independent, peer-reviewed household surveys.",
    programsKicker: "What we do",
    programsHeading: "Core initiatives",
    programsSub: "Four interlocking programs, each designed, delivered, and evaluated in partnership with the communities they serve.",
    programs: [
      { icon: Users, title: "Community-Led Circles", desc: "We support 47 village-based dialogue groups led by survivors, elders, and midwives who are rewriting social norms from within." },
      { icon: Heart, title: "Survivor Healthcare", desc: "Free clinical care, trauma therapy, and reconstructive surgery referrals — delivered locally and with full confidentiality." },
      { icon: GraduationCap, title: "Girls' Education Fund", desc: "Scholarships, mentoring, and safehouse partnerships that keep at-risk girls enrolled and safe through secondary school." },
      { icon: Scale, title: "Advocacy & Policy", desc: "Legal support, policy drafting, and government accountability — we publish an annual Index of enforcement progress." },
    ],
    learnMore: "Learn more",
    impactKicker: "Measured impact",
    impactHeadA: "Numbers we report.",
    impactHeadB: "Independently verified.",
    impactSub: "All figures independently audited by KPMG East Africa and cross-checked against peer-reviewed household surveys (2023–2025).",
    impact: [
      { label: "Lives reached", value: "230K+", trend: "+18% YoY" },
      { label: "Girls in school", value: "4,820", trend: "+42% YoY" },
      { label: "Partner villages", value: "47", trend: "+9 this year" },
      { label: "Countries", value: "12", trend: "East & West Africa" },
    ],
    storiesKicker: "Voices from the field",
    storiesHeading: "Stories that shape our strategy.",
    stories: [
      { name: "Aminata Traoré", role: "Community Leader, Mali", quote: "The Foundation invested in our Circle when nobody else would. Two years later, our village declared itself cutting-free." },
      { name: "Dr. Adaeze Okoye", role: "Lead Clinician, Kenya", quote: "We moved from 40 patients a year to 620 — because the Foundation built the care pathway most NGOs were afraid to build." },
      { name: "Zainab Ahmed", role: "Scholar, Sudan", quote: "I finished secondary school with their scholarship. I am the first woman in my family to read the constitution." },
    ],
    donateKicker: "Support our work",
    donateHeadA: "Every dollar,",
    donateHeadB: "independently tracked.",
    donateSub: "89¢ of every dollar reaches programs. Monthly gifts give us predictability to plan multi-year community partnerships.",
    bullets: ["501(c)(3) tax-deductible (pending)", "Annual audited financial report", "Transparent program dashboards"],
    tiers: ["$25", "$60", "$120"],
    monthly: "monthly · cancel anytime",
    submit: "Become a monthly supporter",
    secure: "Secure · Encrypted · No fees deducted from your gift",
    contactKicker: "Contact",
    contactHeading: "Get in touch.",
    contactSub: "Press, partnerships, survivor support, or grant discussions — we respond within two business days.",
    contacts: [
      { h: "General", v: "hello@motherhippo.org" },
      { h: "Press", v: "press@motherhippo.org" },
      { h: "Partnerships", v: "partners@motherhippo.org" },
      { h: "Offices", v: "Nairobi · Accra · Washington D.C." },
    ],
    rights: "© 2026 Mother Hippo Foundation",
    fnav: ["Privacy", "Financials", "Press Kit"],
  },
  fr: {
    back: "Sélecteur",
    nav: { mission: "Mission", programs: "Programmes", impact: "Impact", stories: "Récits", donate: "Soutenir" },
    badge: "ONG Internationale Enregistrée",
    heroA: "Autonomiser les filles.",
    heroAccent: "Transformer",
    heroB: " les communautés.",
    heroSub: "La Mother Hippo Foundation est dédiée à l'éradication totale des Mutilations Génitales Féminines à travers l'éducation communautaire, les soins aux survivantes et le plaidoyer mondial fondé sur les preuves.",
    support: "Soutenir notre travail",
    report: "Voir le Rapport d'Impact 2025",
    quickStats: [
      { v: "230K+", l: "Vies touchées" },
      { v: "47", l: "Villages" },
      { v: "12", l: "Pays" },
    ],
    verified: "VÉRIFIÉ 2025",
    verifiedP: "Audité indépendamment — 89¢ de chaque dollar finance les programmes.",
    coalition: "En coalition avec",
    partners: ["ONU Femmes", "OMS", "Amnesty", "Equality Now", "Plan International", "UNICEF"],
    missionKicker: "Notre mission stratégique",
    missionHeading: "Un monde où chaque fille écrit sa propre vie.",
    missionP1: "Les MGF touchent plus de 200 millions de femmes et filles vivant aujourd'hui. C'est une crise de santé publique, une violation des droits humains, et l'une des injustices les plus solubles de notre époque.",
    missionP2a: "Nous travaillons à trois niveaux — ",
    missionP2b: "communautaire",
    missionP2c: ", ",
    missionP2d: "clinique",
    missionP2e: ", et ",
    missionP2f: "politique",
    missionP2g: " — avec des partenaires locaux qui connaissent le terrain et des coalitions internationales qui peuvent tenir les gouvernements responsables.",
    missionP3: "Notre engagement 2030 est mesurable : une réduction de 75% de la prévalence des MGF dans nos régions partenaires, suivie par des enquêtes indépendantes évaluées par les pairs.",
    programsKicker: "Ce que nous faisons",
    programsHeading: "Initiatives principales",
    programsSub: "Quatre programmes complémentaires, chacun conçu, déployé et évalué en partenariat avec les communautés qu'il sert.",
    programs: [
      { icon: Users, title: "Cercles Communautaires", desc: "Nous soutenons 47 groupes de dialogue villageois dirigés par des survivantes, aînées et sages-femmes qui réécrivent les normes sociales de l'intérieur." },
      { icon: Heart, title: "Soins aux Survivantes", desc: "Soins cliniques gratuits, thérapie trauma, et orientation en chirurgie reconstructive — livrés localement et en toute confidentialité." },
      { icon: GraduationCap, title: "Fonds Éducation", desc: "Bourses, mentorat et maisons-refuges qui maintiennent les filles à risque scolarisées et en sécurité jusqu'au secondaire." },
      { icon: Scale, title: "Plaidoyer & Politique", desc: "Soutien juridique, rédaction de politiques et reddition de comptes — nous publions un Index annuel des progrès d'application." },
    ],
    learnMore: "En savoir plus",
    impactKicker: "Impact mesuré",
    impactHeadA: "Des chiffres que nous publions.",
    impactHeadB: "Vérifiés indépendamment.",
    impactSub: "Tous les chiffres sont audités indépendamment par KPMG Afrique de l'Est et vérifiés par des enquêtes évaluées par les pairs (2023–2025).",
    impact: [
      { label: "Vies touchées", value: "230K+", trend: "+18% AnA" },
      { label: "Filles à l'école", value: "4 820", trend: "+42% AnA" },
      { label: "Villages partenaires", value: "47", trend: "+9 cette année" },
      { label: "Pays", value: "12", trend: "Afrique de l'Est & Ouest" },
    ],
    storiesKicker: "Voix du terrain",
    storiesHeading: "Des récits qui façonnent notre stratégie.",
    stories: [
      { name: "Aminata Traoré", role: "Leader Communautaire, Mali", quote: "La Fondation a investi dans notre Cercle quand personne d'autre ne le faisait. Deux ans plus tard, notre village s'est déclaré sans excision." },
      { name: "Dr. Adaeze Okoye", role: "Clinicienne Principale, Kenya", quote: "Nous sommes passés de 40 patientes par an à 620 — parce que la Fondation a bâti le parcours de soins que la plupart des ONG n'osaient pas construire." },
      { name: "Zainab Ahmed", role: "Étudiante, Soudan", quote: "J'ai terminé le secondaire grâce à leur bourse. Je suis la première femme de ma famille à lire la constitution." },
    ],
    donateKicker: "Soutenir notre travail",
    donateHeadA: "Chaque dollar,",
    donateHeadB: "suivi indépendamment.",
    donateSub: "89¢ de chaque dollar atteint les programmes. Les dons mensuels nous donnent la prévisibilité nécessaire pour planifier des partenariats pluriannuels.",
    bullets: ["501(c)(3) déductible d'impôt (en cours)", "Rapport financier audité annuel", "Tableaux de bord transparents"],
    tiers: ["25 $", "60 $", "120 $"],
    monthly: "mensuel · annulez à tout moment",
    submit: "Devenir donateur mensuel",
    secure: "Sécurisé · Chiffré · Aucun frais déduit de votre don",
    contactKicker: "Contact",
    contactHeading: "Nous contacter.",
    contactSub: "Presse, partenariats, soutien aux survivantes ou discussions de subventions — nous répondons sous deux jours ouvrables.",
    contacts: [
      { h: "Général", v: "hello@motherhippo.org" },
      { h: "Presse", v: "press@motherhippo.org" },
      { h: "Partenariats", v: "partners@motherhippo.org" },
      { h: "Bureaux", v: "Nairobi · Accra · Washington D.C." },
    ],
    rights: "© 2026 Mother Hippo Foundation",
    fnav: ["Confidentialité", "Finances", "Kit Presse"],
  },
};

export default function PrototypeMinimal() {
  const t = useT(copy);

  return (
    <div
      data-testid="prototype-minimal"
      className="min-h-screen bg-white text-slate-900 font-work selection:bg-[#00A651] selection:text-white"
    >
      {/* Header */}
      <header className="sticky top-0 z-40 bg-white/95 backdrop-blur-md border-b-2 border-[#00A651]">
        <div className="max-w-[1280px] mx-auto px-6 lg:px-10 h-20 flex items-center justify-between gap-4">
          <Link to="/" className="flex items-center gap-2 text-xs tracking-wider text-slate-500 hover:text-[#E3282B] transition-colors" data-testid="back-to-selector">
            <ArrowLeft size={14} />
            <span>{t.back}</span>
          </Link>
          <img src={LOGO} alt="Mother Hippo Foundation" className="h-10 w-auto object-contain" />
          <nav className="hidden md:flex items-center gap-6 font-outfit text-sm">
            <a href="#mission" className="text-slate-600 hover:text-[#00A651] transition-colors">{t.nav.mission}</a>
            <a href="#programs" className="text-slate-600 hover:text-[#00A651] transition-colors">{t.nav.programs}</a>
            <a href="#impact" className="text-slate-600 hover:text-[#00A651] transition-colors">{t.nav.impact}</a>
            <a href="#stories" className="text-slate-600 hover:text-[#00A651] transition-colors">{t.nav.stories}</a>
            <LangToggle testId="minimal-lang-toggle" />
            <a
              href="#donate"
              data-testid="header-donate"
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md bg-[#E3282B] text-white font-semibold hover:bg-[#00A651] transition-colors"
            >
              {t.nav.donate} <ArrowRight size={14} />
            </a>
          </nav>
          <div className="md:hidden">
            <LangToggle testId="minimal-lang-toggle-mobile" />
          </div>
        </div>
      </header>

      {/* HERO */}
      <section className="relative overflow-hidden">
        {/* Bold colored panel behind hero */}
        <div
          aria-hidden
          className="absolute top-0 right-0 h-full w-[55%] bg-gradient-to-br from-[#00A651] to-[#16A34A] -z-0 hidden lg:block"
          style={{ clipPath: "polygon(15% 0, 100% 0, 100% 100%, 0% 100%)" }}
        />
        <div className="relative max-w-[1280px] mx-auto px-6 lg:px-10 pt-20 lg:pt-28 pb-20">
          <div className="grid grid-cols-12 gap-10 items-center">
            <div className="col-span-12 lg:col-span-7 mh-rise">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#E3282B] text-white text-xs font-bold tracking-wide uppercase">
                <span className="h-1.5 w-1.5 rounded-full bg-[#D4A93B]" />
                {t.badge}
              </div>
              <h1
                data-testid="minimal-hero-title"
                className="font-outfit font-semibold text-slate-900 text-5xl md:text-6xl lg:text-7xl leading-[1.02] tracking-tight mt-6"
              >
                {t.heroA}<br />
                <span className="text-[#00A651]">{t.heroAccent}</span>{t.heroB}
              </h1>
              <p className="mt-7 max-w-xl text-lg text-slate-700 leading-relaxed">
                {t.heroSub}
              </p>
              <div className="mt-9 flex flex-wrap items-center gap-4">
                <a
                  href="#donate"
                  data-testid="minimal-donate-cta"
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-md bg-[#E3282B] text-white font-semibold hover:bg-slate-900 transition-colors shadow-lg shadow-[#E3282B]/30"
                >
                  {t.support} <ArrowRight size={16} />
                </a>
                <a
                  href="#impact"
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-md border-2 border-[#00A651] text-[#00A651] font-semibold hover:bg-[#00A651] hover:text-white transition-colors"
                >
                  {t.report}
                </a>
              </div>
              <div className="mt-12 grid grid-cols-3 gap-6 max-w-md">
                {t.quickStats.map((s, i) => (
                  <div key={i} className="border-l-4 pl-4" style={{ borderColor: ["#00A651", "#E3282B", "#D4A93B"][i] }}>
                    <div className="font-outfit text-3xl md:text-4xl font-semibold text-slate-900">{s.v}</div>
                    <div className="text-xs text-slate-600 mt-1.5 font-semibold">{s.l}</div>
                  </div>
                ))}
              </div>
            </div>
            <div className="col-span-12 lg:col-span-5 mh-rise mh-delay-2">
              <div className="relative">
                <div className="absolute -top-4 -left-4 h-full w-full bg-[#D4A93B] rounded-xl -z-0" />
                <img
                  src="https://customer-assets.emergentagent.com/job_equality-hub-7/artifacts/4b9z0d2l_Screenshot%202026-04-30%20at%201.12.43%E2%80%AFPM.png"
                  alt=""
                  className="relative w-full aspect-[4/5] object-cover rounded-xl shadow-2xl"
                />
                <div className="absolute -bottom-6 -left-6 bg-white rounded-xl border-2 border-[#00A651] shadow-xl p-5 max-w-[240px]">
                  <div className="flex items-center gap-2 text-[#00A651]">
                    <CheckCircle2 size={18} />
                    <span className="text-xs font-bold tracking-wide uppercase">{t.verified}</span>
                  </div>
                  <p className="mt-2 text-sm text-slate-700 leading-snug">
                    {t.verifiedP}
                  </p>
                </div>
              </div>
            </div>
          </div>
          {/* Partner logos strip */}
          <div className="mt-24 pt-8 border-t-2 border-[#D4A93B]/60">
            <div className="text-center text-xs uppercase tracking-[0.25em] text-[#E3282B] font-bold mb-6">{t.coalition}</div>
            <div className="flex flex-wrap items-center justify-center gap-x-12 gap-y-4">
              {t.partners.map((p) => (
                <span key={p} className="font-outfit text-slate-500 text-lg font-semibold">
                  {p}
                </span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* MISSION */}
      <section id="mission" className="bg-[#00A651] text-white py-24 lg:py-32 relative overflow-hidden">
        <div
          aria-hidden
          className="absolute -top-40 -right-40 h-[500px] w-[500px] rounded-full bg-[#D4A93B]/40 blur-3xl"
        />
        <div className="max-w-[1280px] mx-auto px-6 lg:px-10 grid grid-cols-12 gap-10 relative">
          <div className="col-span-12 lg:col-span-4">
            <div className="text-xs uppercase tracking-[0.25em] text-[#D4A93B] font-bold">{t.missionKicker}</div>
            <h2 className="font-outfit text-4xl md:text-5xl font-semibold mt-5 leading-[1.05]">
              {t.missionHeading}
            </h2>
          </div>
          <div className="col-span-12 lg:col-span-7 lg:col-start-6 space-y-6 text-white/95 text-lg leading-relaxed">
            <p>{t.missionP1}</p>
            <p>
              {t.missionP2a}<strong className="text-[#D4A93B]">{t.missionP2b}</strong>{t.missionP2c}<strong className="text-[#D4A93B]">{t.missionP2d}</strong>{t.missionP2e}<strong className="text-[#D4A93B]">{t.missionP2f}</strong>{t.missionP2g}
            </p>
            <p>{t.missionP3}</p>
          </div>
        </div>
      </section>

      {/* PROGRAMS */}
      <section id="programs" className="py-24 lg:py-32 bg-white">
        <div className="max-w-[1280px] mx-auto px-6 lg:px-10">
          <div className="flex items-end justify-between flex-wrap gap-6 mb-14">
            <div>
              <div className="text-xs uppercase tracking-[0.25em] text-[#E3282B] font-bold">{t.programsKicker}</div>
              <h2 className="font-outfit text-4xl md:text-5xl font-semibold mt-4 leading-[1.05] text-slate-900">{t.programsHeading}</h2>
            </div>
            <p className="max-w-sm text-slate-600">
              {t.programsSub}
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {t.programs.map((p, i) => {
              const Icon = p.icon;
              const colors = ["#00A651", "#E3282B", "#D4A93B", "#00A651"];
              return (
                <div
                  key={i}
                  data-testid={`minimal-program-${i}`}
                  className="group p-8 lg:p-10 rounded-xl border-2 bg-white hover:shadow-2xl transition-all relative overflow-hidden"
                  style={{ borderColor: `${colors[i]}30` }}
                  onMouseEnter={(e) => (e.currentTarget.style.borderColor = colors[i])}
                  onMouseLeave={(e) => (e.currentTarget.style.borderColor = `${colors[i]}30`)}
                >
                  <div
                    className="absolute top-0 left-0 h-1 w-full"
                    style={{ background: colors[i] }}
                  />
                  <div
                    className="h-14 w-14 rounded-xl inline-flex items-center justify-center"
                    style={{ background: `${colors[i]}15`, color: colors[i] }}
                  >
                    <Icon size={24} />
                  </div>
                  <h3 className="font-outfit text-2xl font-semibold mt-6 text-slate-900">{p.title}</h3>
                  <p className="mt-3 text-slate-600 leading-relaxed">{p.desc}</p>
                  <a
                    href="#"
                    className="mt-6 inline-flex items-center gap-1.5 text-sm font-semibold group-hover:gap-2.5 transition-all"
                    style={{ color: colors[i] }}
                  >
                    {t.learnMore} <ArrowRight size={14} />
                  </a>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* IMPACT */}
      <section id="impact" className="bg-slate-900 text-white py-24 lg:py-32 relative overflow-hidden">
        <div
          aria-hidden
          className="absolute -bottom-40 -left-40 h-[500px] w-[500px] rounded-full bg-[#E3282B]/20 blur-3xl"
        />
        <div
          aria-hidden
          className="absolute -top-20 right-0 h-[400px] w-[400px] rounded-full bg-[#00A651]/20 blur-3xl"
        />
        <div className="max-w-[1280px] mx-auto px-6 lg:px-10 relative">
          <div className="grid grid-cols-12 gap-10 items-end mb-16">
            <div className="col-span-12 lg:col-span-8">
              <div className="text-xs uppercase tracking-[0.25em] text-[#D4A93B] font-bold">{t.impactKicker}</div>
              <h2 className="font-outfit text-4xl md:text-5xl font-semibold mt-4 leading-[1.05]">
                {t.impactHeadA} <br className="hidden md:block" />
                <span className="text-[#00A651]">{t.impactHeadB}</span>
              </h2>
            </div>
            <div className="col-span-12 lg:col-span-4 text-slate-300 leading-relaxed">
              {t.impactSub}
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-[#D4A93B] border-2 border-[#D4A93B] rounded-xl overflow-hidden">
            {t.impact.map((s, i) => {
              const accents = ["#00A651", "#E3282B", "#D4A93B", "#00A651"];
              return (
                <div
                  key={i}
                  className="bg-slate-900 p-8 lg:p-10 hover:bg-slate-800 transition-colors"
                  data-testid={`minimal-impact-${i}`}
                >
                  <div
                    className="font-outfit text-5xl md:text-6xl font-semibold"
                    style={{ color: accents[i] }}
                  >
                    {s.value}
                  </div>
                  <div className="mt-4 text-sm text-white font-semibold">{s.label}</div>
                  <div className="mt-2 text-xs text-[#D4A93B]">{s.trend}</div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* STORIES */}
      <section id="stories" className="py-24 lg:py-32 bg-[#FFF8EC]">
        <div className="max-w-[1280px] mx-auto px-6 lg:px-10">
          <div className="text-xs uppercase tracking-[0.25em] text-[#E3282B] font-bold">{t.storiesKicker}</div>
          <h2 className="font-outfit text-4xl md:text-5xl font-semibold mt-4 leading-[1.05] text-slate-900 max-w-3xl">
            {t.storiesHeading}
          </h2>
          <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-6">
            {t.stories.map((s, i) => {
              const colors = ["#00A651", "#E3282B", "#D4A93B"];
              return (
                <figure
                  key={i}
                  data-testid={`minimal-story-${i}`}
                  className="p-8 rounded-xl bg-white border-2 relative"
                  style={{ borderColor: colors[i] }}
                >
                  <div
                    className="absolute -top-3 left-6 px-3 py-0.5 rounded-full text-[10px] tracking-[0.25em] uppercase font-bold text-white"
                    style={{ background: colors[i] }}
                  >
                    0{i + 1}
                  </div>
                  <blockquote className="font-outfit text-xl text-slate-900 leading-snug mt-2">
                    "{s.quote}"
                  </blockquote>
                  <figcaption className="mt-8 pt-6 border-t border-slate-200">
                    <div className="font-bold text-slate-900">{s.name}</div>
                    <div className="text-sm text-slate-600">{s.role}</div>
                  </figcaption>
                </figure>
              );
            })}
          </div>
        </div>
      </section>

      {/* DONATE */}
      <section id="donate" className="py-24 lg:py-32 bg-gradient-to-br from-[#E3282B] to-[#C62020] text-white relative overflow-hidden">
        <div
          aria-hidden
          className="absolute -top-20 -left-20 h-[400px] w-[400px] rounded-full bg-[#D4A93B]/30 blur-3xl"
        />
        <div className="max-w-[1280px] mx-auto px-6 lg:px-10 grid grid-cols-12 gap-10 items-center relative">
          <div className="col-span-12 lg:col-span-6">
            <div className="text-xs uppercase tracking-[0.25em] text-[#D4A93B] font-bold">{t.donateKicker}</div>
            <h2 className="font-outfit text-4xl md:text-5xl font-semibold mt-4 leading-[1.05]">
              {t.donateHeadA} <br />
              <span className="text-[#D4A93B]">{t.donateHeadB}</span>
            </h2>
            <p className="mt-6 text-white/95 leading-relaxed max-w-md">
              {t.donateSub}
            </p>
            <ul className="mt-8 space-y-3">
              {t.bullets.map((b) => (
                <li key={b} className="flex items-center gap-2.5 text-white">
                  <CheckCircle2 size={18} className="text-[#D4A93B]" />
                  {b}
                </li>
              ))}
            </ul>
          </div>
          <div className="col-span-12 lg:col-span-6">
            <div className="rounded-2xl bg-white text-slate-900 p-6 lg:p-8 shadow-2xl">
              <div className="grid grid-cols-3 gap-3">
                {t.tiers.map((tier, i) => (
                  <button
                    key={i}
                    data-testid={`minimal-donate-tier-${i}`}
                    className={`py-4 rounded-lg border-2 text-lg font-bold transition-colors ${
                      i === 1
                        ? "bg-[#00A651] text-white border-[#00A651]"
                        : "border-slate-300 text-slate-900 bg-white hover:border-[#00A651] hover:text-[#00A651]"
                    }`}
                  >
                    {tier}
                  </button>
                ))}
              </div>
              <div className="mt-3 text-center text-sm text-slate-600">{t.monthly}</div>
              <button
                data-testid="minimal-donate-submit"
                className="mt-5 w-full inline-flex items-center justify-center gap-2 py-4 rounded-lg bg-[#E3282B] text-white font-semibold hover:bg-[#00A651] transition-colors"
              >
                {t.submit} <ArrowRight size={16} />
              </button>
              <div className="mt-4 text-center text-xs text-slate-500">
                {t.secure}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CONTACT */}
      <section className="py-24 lg:py-28 bg-[#FFF8EC] border-t-4 border-[#00A651]">
        <div className="max-w-[1280px] mx-auto px-6 lg:px-10 grid grid-cols-12 gap-10">
          <div className="col-span-12 lg:col-span-5">
            <div className="text-xs uppercase tracking-[0.25em] text-[#E3282B] font-bold">{t.contactKicker}</div>
            <h2 className="font-outfit text-4xl md:text-5xl font-semibold mt-4 leading-[1.05] text-slate-900">
              {t.contactHeading}
            </h2>
            <p className="mt-6 text-slate-700 max-w-md">
              {t.contactSub}
            </p>
          </div>
          <div className="col-span-12 lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-4">
            {t.contacts.map((c, i) => {
              const colors = ["#00A651", "#E3282B", "#D4A93B", "#0F172A"];
              return (
                <div key={i} className="p-6 rounded-xl bg-white border-2 border-slate-200 hover:shadow-lg transition-shadow">
                  <div
                    className="text-xs uppercase tracking-[0.2em] font-bold"
                    style={{ color: colors[i] }}
                  >
                    {c.h}
                  </div>
                  <div className="mt-2 font-outfit text-lg text-slate-900 break-all">{c.v}</div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 text-white py-10">
        <div className="max-w-[1280px] mx-auto px-6 lg:px-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div className="flex items-center gap-4">
            <img src={LOGO} alt="" className="h-10 w-auto object-contain" />
            <div className="text-sm text-white/70">{t.rights}</div>
          </div>
          <div className="flex items-center gap-6 text-sm text-white/70">
            {t.fnav.map((f) => (
              <a key={f} href="#" className="hover:text-[#D4A93B] transition-colors">{f}</a>
            ))}
          </div>
        </div>
      </footer>
    </div>
  );
}
